import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../context/UserContext';
import { FaTimes, FaUpload, FaFileAlt, FaDownload, FaUserTimes, FaExclamationTriangle } from 'react-icons/fa';

const AdminCandidatesPopup = ({ isOpen, onClose }) => {
  const { emailId, isLoggedIn } = useContext(UserContext);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newCandidate, setNewCandidate] = useState({ 
    email: '', 
    party: '',
    candidate_state: 'Federal', // Default to Federal
    first_name: '', // Add first name
    last_name: ''  // Add last name
  });
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [invitationLink, setInvitationLink] = useState('');
  const [copySuccess, setCopySuccess] = useState('');
  
  // New state for CSV upload
  const [csvFile, setCsvFile] = useState(null);
  const [csvFileName, setCsvFileName] = useState('');
  const [uploadStatus, setUploadStatus] = useState('');
  const [batchResults, setBatchResults] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  
  // Australian states and territories for dropdown
  const australianStates = [
    'Federal',
    'New South Wales',
    'Victoria',
    'Queensland',
    'Western Australia',
    'South Australia',
    'Tasmania',
    'Australian Capital Territory',
    'Northern Territory'
  ];

  // Add new state variables inside AdminCandidatesPopup component
  const [duplicates, setDuplicates] = useState([]);
  const [selectedDuplicates, setSelectedDuplicates] = useState(new Set());
  const [deletingDuplicates, setDeletingDuplicates] = useState(false);
  const [unmatchedCandidates, setUnmatchedCandidates] = useState([]);

  // Check if current user is admin and fetch candidates when popup opens
  useEffect(() => {
    if (isOpen) {
      console.log("Popup opened, checking admin access");
      if (!isLoggedIn || !emailId) {
        setError('Please log in to access this feature');
        setLoading(false);
      } else {
        checkAdminAccess();
      }
    }
  }, [isOpen, emailId, isLoggedIn]);

  // Add new useEffect to fetch duplicates
  useEffect(() => {
    if (isOpen) {
      fetchDuplicates();
    }
  }, [isOpen]);

  // Add useEffect to fetch unmatched candidates
  useEffect(() => {
    if (isOpen) {
      fetchUnmatchedCandidates();
    }
  }, [isOpen]);

  // Function to check admin access
  const checkAdminAccess = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        setLoading(false);
        return;
      }
      
      // Pass the email directly from UserContext
      const response = await fetch('/api/check-admin-access', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: emailId })
      });
      
      const data = await response.json();
      console.log("Admin check response:", data);
      
      if (response.ok && data.isAdmin) {
        console.log("Admin access confirmed");
        fetchCandidates();
      } else {
        setError('You do not have permission to access this feature. This feature is only accessible to admin users.');
        setLoading(false);
      }
    } catch (error) {
      console.error("Error checking admin access:", error);
      setError('Error checking admin access: ' + error.message);
      setLoading(false);
    }
  };

  // Function to fetch candidates
  const fetchCandidates = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      console.log("Using token:", token);
      
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        setLoading(false);
        return;
      }

      const response = await fetch('/api/admin/candidates', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: emailId })
      });

      console.log("Response status:", response.status);

      if (response.status === 403) {
        setError('You do not have permission to access this feature');
        setLoading(false);
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch candidates');
      }

      const data = await response.json();
      console.log("Candidates data:", JSON.stringify(data));
      
      // Log each candidate's state
      data.forEach(candidate => {
        console.log(`Candidate ${candidate.email_id} state:`, candidate.candidate_state);
      });
      
      setCandidates(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching candidates:', error);
      setError('Error fetching candidates: ' + error.message);
      setLoading(false);
    }
  };

  // Handle input change for new candidate form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    console.log(`Input changed: ${name} = ${value}`);
    setNewCandidate({
      ...newCandidate,
      [name]: value
    });
    console.log("Updated newCandidate:", {...newCandidate, [name]: value});
  };

  // Handle form submission to add a new candidate
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset messages
    setSuccessMessage('');
    setErrorMessage('');
    setInvitationLink('');
    setCopySuccess('');
    
    // Validate form
    if (!newCandidate.email || !newCandidate.party || !newCandidate.candidate_state || !newCandidate.first_name || !newCandidate.last_name) {
      setErrorMessage('All fields are required');
      return;
    }

    console.log("Submitting candidate with state:", newCandidate.candidate_state);
    console.log("Type of candidate_state:", typeof newCandidate.candidate_state);
    console.log("Full newCandidate object:", JSON.stringify(newCandidate));

    try {
      const token = localStorage.getItem('token');
      
      const requestBody = {
        email: newCandidate.email,
        party: newCandidate.party,
        candidate_state: newCandidate.candidate_state,
        first_name: newCandidate.first_name,
        last_name: newCandidate.last_name,
        adminEmail: emailId
      };
      
      console.log("Request body:", JSON.stringify(requestBody));
      
      const response = await fetch('/api/admin/add-candidate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      console.log("Response data:", JSON.stringify(data));

      if (!response.ok) {
        throw new Error(data.error || 'Failed to add candidate');
      }

      // Success
      setSuccessMessage(`Candidate ${newCandidate.email} added successfully${data.emailSent ? ' and invitation email sent' : ''}`);
      
      // Clear form
      setNewCandidate({ 
        email: '', 
        party: '',
        candidate_state: 'Federal',
        first_name: '',
        last_name: ''
      });
      
      // Refresh candidates list
      fetchCandidates();
      
    } catch (error) {
      console.error('Error adding candidate:', error);
      setErrorMessage(error.message || 'Error adding candidate. Please try again.');
    }
  };

  // Handle CSV file selection
  const handleCsvFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCsvFile(file);
      setCsvFileName(file.name);
      setUploadStatus('');
      setBatchResults(null);
    }
  };

  // Download CSV template
  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch('/api/admin/candidate-template', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to download template');
      }
      
      // Get the blob from the response
      const blob = await response.blob();
      
      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary link element
      const a = document.createElement('a');
      a.href = url;
      a.download = 'candidate_template.csv';
      
      // Append to the document, click it, and remove it
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
    } catch (error) {
      console.error('Error downloading template:', error);
      setUploadStatus('Error downloading template: ' + error.message);
    }
  };

  // Handle CSV upload for batch processing
  const handleCsvUpload = async (e) => {
    e.preventDefault();
    
    if (!csvFile) {
      setUploadStatus('Please select a CSV file first');
      return;
    }
    
    // Reset messages
    setSuccessMessage('');
    setErrorMessage('');
    setInvitationLink('');
    setCopySuccess('');
    setUploadStatus('Uploading and processing...');
    setIsUploading(true);
    
    try {
      const token = localStorage.getItem('token');
      
      const formData = new FormData();
      formData.append('csvFile', csvFile);
      formData.append('adminEmail', emailId);
      
      const response = await fetch('/api/admin/batch-add-candidates', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();
      console.log("Batch upload response:", data);

      if (!response.ok) {
        throw new Error(data.error || 'Failed to process CSV file');
      }

      // Success
      setUploadStatus('CSV processed successfully');
      setBatchResults(data.results);
      
      // Clear file input
      setCsvFile(null);
      setCsvFileName('');
      
      // Refresh candidates list
      fetchCandidates();
      
    } catch (error) {
      console.error('Error processing CSV:', error);
      setUploadStatus(`Error: ${error.message || 'Failed to process CSV file'}`);
    } finally {
      setIsUploading(false);
    }
  };

  // Copy invitation link to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(invitationLink)
      .then(() => {
        setCopySuccess('Copied!');
        setTimeout(() => setCopySuccess(''), 2000);
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
        setCopySuccess('Failed to copy');
      });
  };

  // Generate invitation link for a candidate
  const generateInvitationLink = async (candidate) => {
    try {
      setSuccessMessage('');
      setErrorMessage('');
      setInvitationLink('');
      setCopySuccess('');
      
      const token = localStorage.getItem('token');
      
      const response = await fetch('/api/admin/generate-invitation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          email: candidate.email_id,
          party: candidate.party,
          candidate_state: candidate.candidate_state || 'Federal',
          adminEmail: emailId
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to generate invitation link');
      }

      // Set the invitation link
      setInvitationLink(data.invitationUrl);
      setSuccessMessage(`Generated invitation link for ${candidate.email_id}`);
    } catch (error) {
      console.error('Error generating invitation link:', error);
      setErrorMessage(error.message || 'Error generating invitation link');
    }
  };

  // Add these new functions to the component
  const fetchDuplicates = async () => {
    try {
      const response = await fetch('/api/admin/duplicate-politicians');
      const data = await response.json();
      setDuplicates(data);
    } catch (error) {
      console.error("Error fetching duplicates:", error);
      setErrorMessage("Error fetching potential duplicates");
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedDuplicates.size === 0) return;

    if (!window.confirm(`Are you sure you want to delete ${selectedDuplicates.size} selected politicians?`)) {
      return;
    }

    try {
      setDeletingDuplicates(true);
      const response = await fetch('/api/admin/delete-duplicates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          politicianIds: Array.from(selectedDuplicates)
        })
      });

      const data = await response.json();
      if (data.success) {
        setSuccessMessage(data.message);
        setSelectedDuplicates(new Set());
        fetchDuplicates(); // Refresh the duplicates list
      } else {
        setErrorMessage(data.error || "Error deleting duplicates");
      }
    } catch (error) {
      console.error("Error deleting duplicates:", error);
      setErrorMessage("Error deleting duplicates");
    } finally {
      setDeletingDuplicates(false);
    }
  };

  // Function to fetch unmatched candidates
  const fetchUnmatchedCandidates = async () => {
    try {
      const response = await fetch('/api/unmatched-candidates');

      if (!response.ok) {
        throw new Error('Failed to fetch unmatched candidates');
      }

      const data = await response.json();
      setUnmatchedCandidates(data.data);
    } catch (error) {
      console.error('Error fetching unmatched candidates:', error);
    }
  };

  // Add delete function for unmatched candidates
  const handleDeleteUnmatched = async (id) => {
    if (!window.confirm('Are you sure you want to delete this unmatched candidate?')) {
      return;
    }

    try {
      const response = await fetch(`/api/unmatched-candidates/${id}`, {
        method: 'DELETE'
      });

      const data = await response.json();

      if (response.ok) {
        setSuccessMessage(data.message);
        // Refresh the unmatched candidates list
        fetchUnmatchedCandidates();
      } else {
        setErrorMessage(data.error || 'Error deleting candidate');
      }
    } catch (error) {
      console.error('Error deleting unmatched candidate:', error);
      setErrorMessage('Error deleting candidate');
    }
  };

  // If the popup is not open, don't render anything
  if (!isOpen) return null;

  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 999,
    }}>
      <div style={{
        backgroundColor: "#fff",
        borderRadius: "10px",
        padding: "20px",
        width: "95%",
        maxWidth: "800px",
        maxHeight: "80vh",
        overflowY: "auto",
        boxShadow: "0px 4px 10px rgba(0,0,0,0.2)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "20px" }}>
          <h3 style={{ margin: 0 }}>Candidate Management</h3>
          <FaTimes onClick={onClose} style={{ cursor: "pointer", fontSize: "1.5rem" }} />
        </div>
        
        {loading ? (
          <p style={{ textAlign: "center" }}>Loading...</p>
        ) : error ? (
          <div style={{ 
            backgroundColor: "#f8d7da", 
            color: "#721c24", 
            padding: "10px", 
            borderRadius: "5px", 
            marginBottom: "15px" 
          }}>
            <p style={{ margin: 0 }}>{error}</p>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
                        {/* Unmatched Candidates Section */}
                        <div style={{ 
              backgroundColor: "#f8f9fa", 
              borderRadius: "5px", 
              padding: "15px", 
              marginBottom: "20px" 
            }}>
              <h4 style={{ marginTop: 0, marginBottom: "15px" }}>
                <FaExclamationTriangle style={{ color: "#f0ad4e", marginRight: "8px" }} />
                Unmatched AEC Candidates
              </h4>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ backgroundColor: "#007bff", color: "#fff" }}>
                      <th style={{ padding: "10px", textAlign: "left" }}>Action</th>
                      <th style={{ padding: "10px", textAlign: "left" }}>Name</th>
                      <th style={{ padding: "10px", textAlign: "left" }}>Party</th>
                      <th style={{ padding: "10px", textAlign: "left" }}>Chamber</th>
                      <th style={{ padding: "10px", textAlign: "left" }}>State</th>
                      <th style={{ padding: "10px", textAlign: "left" }}>Email</th>
                      <th style={{ padding: "10px", textAlign: "left" }}>Created At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unmatchedCandidates.length === 0 ? (
                      <tr>
                        <td colSpan="7" style={{ padding: "20px", textAlign: "center", color: "#6c757d" }}>
                          No unmatched pending candidates found
                        </td>
                      </tr>
                    ) : (
                      unmatchedCandidates.map((candidate, index) => (
                        <tr key={index} style={{ borderBottom: "1px solid #dee2e6" }}>
                          <td style={{ padding: "10px" }}>
                            <button
                              onClick={() => handleDeleteUnmatched(candidate.id)}
                              style={{
                                backgroundColor: "#dc3545",
                                color: "white",
                                border: "none",
                                padding: "5px 10px",
                                borderRadius: "5px",
                                cursor: "pointer"
                              }}
                            >
                              Delete
                            </button>
                          </td>
                          <td style={{ padding: "10px" }}>{candidate.name}</td>
                          <td style={{ padding: "10px" }}>{candidate.party}</td>
                          <td style={{ padding: "10px" }}>{candidate.chamber}</td>
                          <td style={{ padding: "10px" }}>{candidate.state}</td>
                          <td style={{ padding: "10px" }}>{candidate.email_id}</td>
                          <td style={{ padding: "10px" }}>
                            {new Date(candidate.created_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            
            {/* Add New Candidate Form */}
            <div style={{ 
              backgroundColor: "#f8f9fa", 
              borderRadius: "5px", 
              padding: "15px", 
              marginBottom: "20px" 
            }}>
              <h4 style={{ marginTop: 0, marginBottom: "15px" }}>Add New Candidate</h4>
              
              {successMessage && (
                <div style={{ 
                  backgroundColor: "#d4edda", 
                  color: "#155724", 
                  padding: "10px", 
                  borderRadius: "5px", 
                  marginBottom: "15px" 
                }}>
                  <p style={{ margin: 0 }}>{successMessage}</p>
                </div>
              )}
              
              {errorMessage && (
                <div style={{ 
                  backgroundColor: "#f8d7da", 
                  color: "#721c24", 
                  padding: "10px", 
                  borderRadius: "5px", 
                  marginBottom: "15px" 
                }}>
                  <p style={{ margin: 0 }}>{errorMessage}</p>
                </div>
              )}
              
              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: "15px" }}>
                  <label style={{ 
                    display: "block", 
                    marginBottom: "5px", 
                    fontWeight: "bold" 
                  }} htmlFor="first_name">
                    First Name
                  </label>
                  <input
                    style={{ 
                      width: "100%", 
                      padding: "8px", 
                      borderRadius: "5px", 
                      border: "1px solid #ced4da" 
                    }}
                    id="first_name"
                    type="text"
                    placeholder="First Name"
                    name="first_name"
                    value={newCandidate.first_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div style={{ marginBottom: "15px" }}>
                  <label style={{ 
                    display: "block", 
                    marginBottom: "5px", 
                    fontWeight: "bold" 
                  }} htmlFor="last_name">
                    Last Name
                  </label>
                  <input
                    style={{ 
                      width: "100%", 
                      padding: "8px", 
                      borderRadius: "5px", 
                      border: "1px solid #ced4da" 
                    }}
                    id="last_name"
                    type="text"
                    placeholder="Last Name"
                    name="last_name"
                    value={newCandidate.last_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div style={{ marginBottom: "15px" }}>
                  <label style={{ 
                    display: "block", 
                    marginBottom: "5px", 
                    fontWeight: "bold" 
                  }} htmlFor="email">
                    Email Address
                  </label>
                  <input
                    style={{ 
                      width: "100%", 
                      padding: "8px", 
                      borderRadius: "5px", 
                      border: "1px solid #ced4da" 
                    }}
                    id="email"
                    type="email"
                    placeholder="candidate@example.com"
                    name="email"
                    value={newCandidate.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div style={{ marginBottom: "15px" }}>
                  <label style={{ 
                    display: "block", 
                    marginBottom: "5px", 
                    fontWeight: "bold" 
                  }} htmlFor="party">
                    Party
                  </label>
                  <input
                    style={{ 
                      width: "100%", 
                      padding: "8px", 
                      borderRadius: "5px", 
                      border: "1px solid #ced4da" 
                    }}
                    id="party"
                    type="text"
                    placeholder="Party Name"
                    name="party"
                    value={newCandidate.party}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div style={{ marginBottom: "15px" }}>
                  <label style={{ 
                    display: "block", 
                    marginBottom: "5px", 
                    fontWeight: "bold" 
                  }} htmlFor="candidate_state">
                    Electoral State
                  </label>
                  <select
                    style={{ 
                      width: "100%", 
                      padding: "8px", 
                      borderRadius: "5px", 
                      border: "1px solid #ced4da" 
                    }}
                    id="candidate_state"
                    name="candidate_state"
                    value={newCandidate.candidate_state}
                    onChange={handleInputChange}
                    required
                  >
                    {australianStates.map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <button
                    style={{ 
                      backgroundColor: "#007bff", 
                      color: "white", 
                      border: "none", 
                      padding: "8px 15px", 
                      borderRadius: "5px", 
                      cursor: "pointer" 
                    }}
                    type="submit"
                  >
                    Add Candidate
                  </button>
                </div>
              </form>
            </div>
            
            {/* Candidates List */}
            <div style={{ 
              backgroundColor: "#f8f9fa", 
              borderRadius: "5px", 
              padding: "15px", 
              marginBottom: "20px" 
            }}>
              <h4 style={{ marginTop: 0, marginBottom: "15px" }}>Existing Candidates</h4>
              
              {candidates.length === 0 ? (
                <p style={{ textAlign: "center" }}>No candidates found.</p>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ backgroundColor: "#007bff", color: "#fff" }}>
                        <th style={{ padding: "10px", textAlign: "left" }}>Email</th>
                        <th style={{ padding: "10px", textAlign: "left" }}>Party</th>
                        <th style={{ padding: "10px", textAlign: "left" }}>Electoral State</th>
                        <th style={{ padding: "10px", textAlign: "center" }}>Status</th>
                        <th style={{ padding: "10px", textAlign: "center" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {candidates.map((candidate, index) => (
                        <tr key={index} style={{ borderBottom: "1px solid #dee2e6" }}>
                          <td style={{ padding: "10px" }}>{candidate.email_id}</td>
                          <td style={{ padding: "10px" }}>{candidate.party}</td>
                          <td style={{ padding: "10px" }}>
                            {candidate.candidate_state || 'Federal'}
                            {!candidate.candidate_state && <span style={{ color: 'red' }}> (Default)</span>}
                          </td>
                          <td style={{ padding: "10px", textAlign: "center" }}>
                            {candidate.registered ? (
                              <span style={{ 
                                backgroundColor: "#d4edda", 
                                color: "#155724", 
                                padding: "3px 8px", 
                                borderRadius: "10px", 
                                fontSize: "0.8rem" 
                              }}>
                                Registered
                              </span>
                            ) : (
                              <span style={{ 
                                backgroundColor: "#fff3cd", 
                                color: "#856404", 
                                padding: "3px 8px", 
                                borderRadius: "10px", 
                                fontSize: "0.8rem" 
                              }}>
                                Pending
                              </span>
                            )}
                          </td>
                          <td style={{ padding: "10px", textAlign: "center" }}>
                            <button
                              onClick={() => generateInvitationLink(candidate)}
                              style={{ 
                                backgroundColor: "transparent", 
                                color: "#007bff", 
                                border: "none", 
                                cursor: "pointer" 
                              }}
                            >
                              Generate Link
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            
            {/* Invitation Link Section */}
            {invitationLink && (
              <div style={{ 
                backgroundColor: "#f8f9fa", 
                borderRadius: "5px", 
                padding: "15px", 
                marginBottom: "20px" 
              }}>
                <h4 style={{ marginTop: 0, marginBottom: "15px" }}>Invitation Link</h4>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <input
                    type="text"
                    value={invitationLink}
                    readOnly
                    style={{ 
                      flex: 1, 
                      padding: "8px", 
                      borderRadius: "5px", 
                      border: "1px solid #ced4da",
                      marginRight: "10px" 
                    }}
                  />
                  <button
                    onClick={copyToClipboard}
                    style={{ 
                      backgroundColor: "#6c757d", 
                      color: "white", 
                      border: "none", 
                      padding: "8px 15px", 
                      borderRadius: "5px", 
                      cursor: "pointer" 
                    }}
                  >
                    {copySuccess || 'Copy'}
                  </button>
                </div>
                <p style={{ 
                  fontSize: "0.9rem", 
                  color: "#6c757d", 
                  marginTop: "10px", 
                  marginBottom: 0 
                }}>
                  Share this link with the candidate. The link will expire after 24 hours.
                </p>
              </div>
            )}

            {/* Potential Duplicates Section */}
            {duplicates.length > 0 && (
              <div style={{ 
                backgroundColor: "#f8f9fa", 
                borderRadius: "5px", 
                padding: "15px", 
                marginBottom: "20px" 
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
                  <h4 style={{ margin: 0 }}>
                    <FaExclamationTriangle style={{ color: "#f0ad4e", marginRight: "8px" }} />
                    Potential Duplicates
                  </h4>
                  <button
                    onClick={handleDeleteSelected}
                    disabled={selectedDuplicates.size === 0 || deletingDuplicates}
                    style={{ 
                      backgroundColor: "#dc3545", 
                      color: "white", 
                      border: "none", 
                      padding: "8px 15px", 
                      borderRadius: "5px", 
                      cursor: selectedDuplicates.size === 0 ? "not-allowed" : "pointer",
                      opacity: selectedDuplicates.size === 0 ? 0.7 : 1,
                      display: "flex",
                      alignItems: "center",
                      gap: "8px"
                    }}
                  >
                    <FaUserTimes />
                    {deletingDuplicates ? "Deleting..." : `Delete Selected (${selectedDuplicates.size})`}
                  </button>
                </div>

                {duplicates.map((group, groupIndex) => (
                  <div key={groupIndex} style={{ 
                    border: "1px solid #dee2e6", 
                    borderRadius: "5px", 
                    marginBottom: "15px", 
                    padding: "15px" 
                  }}>
                    <h5 style={{ marginTop: 0, marginBottom: "10px" }}>
                      Verified Politician: {group.ranked.name}
                    </h5>
                    <div style={{ marginLeft: "20px" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr style={{ backgroundColor: "#e9ecef" }}>
                            <th style={{ padding: "8px", textAlign: "left" }}>Select</th>
                            <th style={{ padding: "8px", textAlign: "left" }}>Name</th>
                            <th style={{ padding: "8px", textAlign: "left" }}>Party</th>
                            <th style={{ padding: "8px", textAlign: "left" }}>State</th>
                            <th style={{ padding: "8px", textAlign: "left" }}>Constituency</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.potentialDuplicates.map((duplicate, index) => (
                            <tr key={index} style={{ borderBottom: "1px solid #dee2e6" }}>
                              <td style={{ padding: "8px" }}>
                                <input
                                  type="checkbox"
                                  checked={selectedDuplicates.has(duplicate.id)}
                                  onChange={(e) => {
                                    const newSelected = new Set(selectedDuplicates);
                                    if (e.target.checked) {
                                      newSelected.add(duplicate.id);
                                    } else {
                                      newSelected.delete(duplicate.id);
                                    }
                                    setSelectedDuplicates(newSelected);
                                  }}
                                />
                              </td>
                              <td style={{ padding: "8px" }}>{duplicate.name}</td>
                              <td style={{ padding: "8px" }}>{duplicate.party}</td>
                              <td style={{ padding: "8px" }}>{duplicate.state}</td>
                              <td style={{ padding: "8px" }}>{duplicate.constituency}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            )}
                        {/* CSV Upload Section */}
                        <div style={{ 
              backgroundColor: "#f8f9fa", 
              borderRadius: "5px", 
              padding: "15px", 
              marginBottom: "20px" 
            }}>
              <h4 style={{ marginTop: 0, marginBottom: "15px" }}>Batch Upload Candidates</h4>
              
              <p style={{ fontSize: "0.9rem", marginBottom: "15px" }}>
                Upload a CSV file with candidate information. The CSV should have the following columns: 
                <strong>email</strong>, <strong>party</strong>, and <strong>state</strong> (optional, defaults to Federal).
                <button 
                  onClick={downloadTemplate}
                  style={{ 
                    backgroundColor: "transparent", 
                    color: "#007bff", 
                    border: "none", 
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    marginLeft: "10px",
                    fontSize: "0.9rem",
                    padding: "2px 5px"
                  }}
                >
                  <FaDownload size={14} />
                  Download Template
                </button>
              </p>
              
              <div style={{ 
                border: "2px dashed #ced4da", 
                borderRadius: "5px", 
                padding: "20px", 
                textAlign: "center",
                marginBottom: "15px"
              }}>
                <label htmlFor="csvFileInput" style={{ 
                  display: "block", 
                  cursor: "pointer" 
                }}>
                  <FaFileAlt style={{ fontSize: "2rem", color: "#6c757d", marginBottom: "10px" }} />
                  <p style={{ margin: 0, marginBottom: "5px" }}>
                    {csvFileName ? csvFileName : "Click to select CSV file"}
                  </p>
                  <p style={{ margin: 0, fontSize: "0.8rem", color: "#6c757d" }}>
                    or drag and drop file here
                  </p>
                </label>
                <input
                  id="csvFileInput"
                  type="file"
                  accept=".csv"
                  onChange={handleCsvFileChange}
                  style={{ display: "none" }}
                />
              </div>
              
              <div style={{ textAlign: "center" }}>
                <button
                  onClick={handleCsvUpload}
                  disabled={!csvFile || isUploading}
                  style={{ 
                    backgroundColor: "#28a745", 
                    color: "white", 
                    border: "none", 
                    padding: "8px 15px", 
                    borderRadius: "5px", 
                    cursor: csvFile && !isUploading ? "pointer" : "not-allowed",
                    opacity: csvFile && !isUploading ? 1 : 0.7,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px"
                  }}
                >
                  <FaUpload />
                  {isUploading ? "Processing..." : "Upload and Process"}
                </button>
              </div>
              
              {uploadStatus && (
                <div style={{ 
                  backgroundColor: uploadStatus.includes('Error') ? "#f8d7da" : "#d4edda", 
                  color: uploadStatus.includes('Error') ? "#721c24" : "#155724", 
                  padding: "10px", 
                  borderRadius: "5px", 
                  marginTop: "15px" 
                }}>
                  <p style={{ margin: 0 }}>{uploadStatus}</p>
                </div>
              )}
              
              {batchResults && (
                <div style={{ marginTop: "15px" }}>
                  <h5 style={{ marginBottom: "10px" }}>Processing Results:</h5>
                  <div style={{ 
                    backgroundColor: "#e9ecef", 
                    padding: "10px", 
                    borderRadius: "5px", 
                    fontSize: "0.9rem" 
                  }}>
                    <p style={{ margin: 0, marginBottom: "5px" }}>
                      <strong>Total processed:</strong> {batchResults.total}
                    </p>
                    <p style={{ margin: 0, marginBottom: "5px" }}>
                      <strong>Successfully added:</strong> {batchResults.success}
                    </p>
                    <p style={{ margin: 0, marginBottom: "5px" }}>
                      <strong>Failed:</strong> {batchResults.failed}
                    </p>
                    {batchResults.emailsSent > 0 && (
                      <p style={{ margin: 0, marginBottom: "5px" }}>
                        <strong>Invitation emails sent:</strong> {batchResults.emailsSent}
                      </p>
                    )}
                    
                    {batchResults.errors && batchResults.errors.length > 0 && (
                      <div style={{ marginTop: "10px" }}>
                        <p style={{ margin: 0, marginBottom: "5px" }}><strong>Errors:</strong></p>
                        <ul style={{ margin: 0, paddingLeft: "20px" }}>
                          {batchResults.errors.map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCandidatesPopup; 